/**
 * Shared UI building blocks for showing/copying apigen request examples.
 *
 * These are used by both the Request History detail modal (to reproduce a
 * recorded call) and the developer Docs tab (to show canonical request/response
 * examples), so they live in one place instead of being duplicated.
 *
 * Pure (non-component) helpers live in `curl.ts`.
 */
import { ReactNode, useState } from 'react';

export function ActionIcon({ icon }: { icon: 'play' | 'copy' | 'check' | 'search' }) {
  return (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {icon === 'play' && (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      )}
      {icon === 'copy' && (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
      )}
      {icon === 'check' && (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      )}
      {icon === 'search' && (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
      )}
    </svg>
  );
}

export function ActionButton({
  children,
  onClick,
  icon,
}: {
  children: ReactNode;
  onClick: () => void;
  icon: 'play' | 'copy' | 'search';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:border-indigo-300 transition-colors"
    >
      <ActionIcon icon={icon} />
      {children}
    </button>
  );
}

/** Bare icon-only copy button, for tight spots like table cells and ID rows. */
export function InlineCopyButton({ value, title }: { value: string; title?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title={title || 'Copy'}
      onClick={() => {
        void navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="text-slate-400 hover:text-indigo-600 shrink-0"
    >
      <ActionIcon icon={copied ? 'check' : 'copy'} />
    </button>
  );
}

export function CopyActionButton({
  children,
  value,
  icon,
}: {
  children: ReactNode;
  value: string;
  icon: 'play' | 'copy';
}) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:border-indigo-300 transition-colors"
    >
      <ActionIcon icon={copied ? 'check' : icon} />
      {copied ? 'Copied' : children}
    </button>
  );
}
